/**
 * @file index.tsx
 *
 * @fileoverview Renders landing page blog teasers.
 */

import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import AnchorDiv from '~/jobiqo/components/AnchorDiv';
import { getReducedSummary } from '~/jobiqo/components/Blog/utils';
import Image from '~/jobiqo/components/Image';
import Link from '~/jobiqo/components/Link';
import { BlogPost } from '~/jobiqo/models/blog';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Grid } from '~/vcl/atoms/06-layout/04-grid/';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { BlogTeaser } from '~/vcl/organisms/11-blog/01-blog-teaser';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { AddIcon } from '~/vcl/icons/Add';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import NextLink from 'next/link';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const ShowAllBlogsButton = ({ frontendTheme }) => (
  <Link href="/blog" prefetch={false} asChild>
    <LinkButton
      linkElement={NextLink}
      buttonStyle={
        frontendTheme.LANDING_PAGES.BLOG_TEASERS.SHOW_ALL_BUTTON_STYLE
      }
      raised={false}
    >
      <AddIcon title="Show more" />
      <span>
        <FormattedMessage
          id="landing_blogs_link"
          defaultMessage="See all articles"
        />
      </span>
    </LinkButton>
  </Link>
);

interface BlogTeasersProps {
  /**
   * The blog teasers row.
   */
  row: { posts: BlogPost[]; introText?: string; introTextSecondary?: string };
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export default function BlogTeasers({
  row: { posts, introText, introTextSecondary },
  maxContainerSize,
}: BlogTeasersProps) {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);

  const { ADVERTORIAL_LABEL } = getCommonTranslations();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    setBlogPosts(posts);
  }, [posts]);

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.BLOG_TEASERS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        className="jd-lp-blog-teasers-intro"
        color={
          frontendTheme.LANDING_PAGES.BLOG_TEASERS.INTRO_COLOR ?? 'primary'
        }
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  return (
    <Spacing paddingTop={8} paddingBottom={8} marginBottom={0}>
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <FlexItems justifyContent="space-between" alignItems="center">
          <FlexItem>
            {introText && (
              <>
                <ParagraphInfo>{introText}</ParagraphInfo>
                <Spacing size="medium" />
              </>
            )}
            {introTextSecondary && (
              <>
                <Text
                  tag="h2"
                  fontSize={[5, 6]}
                  lineHeight={[5, 6]}
                  {...(frontendTheme.LANDING_PAGES.BLOG_TEASERS
                    .INTRO_SECONDARY_COLOR
                    ? {
                        color:
                          frontendTheme.LANDING_PAGES.BLOG_TEASERS
                            .INTRO_SECONDARY_COLOR,
                      }
                    : null)}
                >
                  {introTextSecondary}
                </Text>
                <Spacing size="large" />
              </>
            )}
          </FlexItem>
          <HideMobile>
            <FlexItem justifySelf="flex-end">
              <ShowAllBlogsButton frontendTheme={frontendTheme} />
            </FlexItem>
          </HideMobile>
        </FlexItems>
        <Grid
          className="lp-blog-teasers-grid"
          columns={[1, maxContainerSize === 'small' ? 2 : 3]}
          margin={0}
        >
          {blogPosts.map(
            ({
              id,
              image,
              title,
              category,
              url,
              summary,
              featured,
            }: BlogPost) => (
              <Cell key={id} display="grid">
                <AnchorDiv route={url.path}>
                  <BlogTeaser
                    {...(image?.url && {
                      banner: (
                        <Image
                          src={image.url}
                          height={300}
                          width={600}
                          alt={image.alt ?? ''}
                          {...(image.title && { title: image.title })}
                        />
                      ),
                    })}
                    title={
                      <Link href={url.path} target="_blank">
                        {title}
                      </Link>
                    }
                    content={
                      summary ? (
                        <WYSIWYGText
                          format="full_html"
                          content={getReducedSummary(summary)}
                        />
                      ) : (
                        ''
                      )
                    }
                    {...(category && { categories: [category.label] })}
                    actions={
                      <Link
                        className="jd-blog-see-more"
                        href={url.path}
                        target="_blank"
                      >
                        <FormattedMessage
                          id="blog_see_more"
                          defaultMessage="See more"
                        />
                      </Link>
                    }
                    {...(featured && {
                      advertorialLabel: ADVERTORIAL_LABEL,
                    })}
                  />
                </AnchorDiv>
              </Cell>
            ),
          )}
        </Grid>
      </Main>
    </Spacing>
  );
}
export { BlogTeasers };
